import {
  Fragment,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil, Play, Trash2, X } from "lucide-react";
import { NavPill } from "@shared/ui/NavPill";
import { Training, TrainingsProxy } from "@entities/training";
import { getMediaUrl } from "@shared/helpers/getMediaUrl";
import { useStartTraining } from "@shared/hooks/trainingsNavigation/startTraining";
import { MuscleService } from "@entities/muscle";
import { ExercisesAccordion } from "@widgets/trainingModals/formTrainingModal/exercisesAccordion";
import { FormTrainingModal } from "@widgets/trainingModals/formTrainingModal/modal";
import { useUpdateTraining } from "@entities/training/hooks/crud.update";
import { useDeleteTraining } from "@entities/training/hooks";
import "./training-details-overlay.css";

type DetailsAction = "delete" | "edit" | "start";
type ActiveDetailsAction = DetailsAction | null;
const CLOSE_ANIMATION_MS = 260;
const DRAG_CLOSE_DISTANCE = 92;

type ExerciseBaseParams = {
  exerciseName: string;
  exercise_id: number;
  instanceId?: string;
  order: number;
  imageUrl?: string;
  primaryMuscle?: string;
  secondaryMuscles?: string[];
};

type CardioExerciseParams = ExerciseBaseParams & {
  type: "cardio";
  duration?: number;
  rest_time?: number;
};

type StrengthExerciseParams = ExerciseBaseParams & {
  type: "strength";
  sets?: number;
  reps?: number;
  duration?: number;
  rest_time?: number;
};

type ExerciseParams = CardioExerciseParams | StrengthExerciseParams;

export type TrainingDetailsMuscleItem = {
  code: string;
  icon: string;
  title: string;
};

type TrainingDetailsMuscleRenderer = (
  muscle: TrainingDetailsMuscleItem,
) => ReactNode;

function getExerciseCountLabel(count: number): string {
  const lastTwoDigits = count % 100;
  const lastDigit = count % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return `${count} упражнений`;
  }

  if (lastDigit === 1) {
    return `${count} упражнение`;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${count} упражнения`;
  }

  return `${count} упражнений`;
}

function getCoverUrl(training: Training): string | undefined {
  return (
    getMediaUrl(training.media?.cover) ||
    getMediaUrl(training.exercises?.[0]?.exercise_detail.media?.icon)
  );
}

function safeExpandMuscleCode(code: string): string[] {
  try {
    return MuscleService.getMuscleCodesFromGroupCode(code);
  } catch {
    return [code];
  }
}

function getTrainingMuscleItems(
  training: Training,
  muscleItems: TrainingDetailsMuscleItem[],
): TrainingDetailsMuscleItem[] {
  const selectedCodes = new Set<string>();

  training.muscle_group_codes?.forEach((code) => {
    safeExpandMuscleCode(code).forEach((muscleCode) =>
      selectedCodes.add(muscleCode),
    );
  });

  training.exercises?.forEach((exercise) => {
    exercise.exercise_detail.muscle_group_codes?.forEach((code) => {
      safeExpandMuscleCode(code).forEach((muscleCode) =>
        selectedCodes.add(muscleCode),
      );
    });
  });

  return muscleItems.filter((muscle) => selectedCodes.has(muscle.code));
}

function mapTrainingExerciseToParams(
  exercise: Training["exercises"][number],
  index: number,
): ExerciseParams {
  const muscleGroups = MuscleService.getMuscleLabelsParentsInfo(
    exercise.exercise_detail.muscle_group_codes ?? [],
  );
  const baseParams = {
    exerciseName: exercise.exercise_detail.name,
    exercise_id: exercise.exercise_detail.id_exercise,
    instanceId: `${exercise.exercise_detail.id_exercise}-${exercise.order}-${index}`,
    order: index + 1,
    imageUrl: getMediaUrl(exercise.exercise_detail.media?.icon),
    primaryMuscle: muscleGroups[0]?.name,
    secondaryMuscles: muscleGroups.slice(1, 4).map((muscle) => muscle.name),
  };

  if (exercise.exercise_detail.type === "cardio") {
    return {
      ...baseParams,
      type: "cardio",
      duration: exercise.duration ?? undefined,
      rest_time: exercise.rest_time ?? undefined,
    };
  }

  return {
    ...baseParams,
    type: "strength",
    sets: exercise.sets ?? undefined,
    reps: exercise.reps ?? undefined,
    duration: exercise.duration ?? undefined,
    rest_time: exercise.rest_time ?? undefined,
  };
}

const WorkoutInfoHeader = ({ training }: { training: Training }) => {
  const coverUrl = getCoverUrl(training);
  const description = training.description?.trim();

  return (
    <section
      className="training-details-hero"
      aria-label="Информация о тренировке"
    >
      <div className="training-details-cover">
        {coverUrl ? <img src={coverUrl} alt="" /> : null}
      </div>
      <div className="training-details-hero-copy">
        <h2>{training.name}</h2>
        {description && <p>{description}</p>}
        <div className="training-details-summary">
          <span>{getExerciseCountLabel(training.exercises.length)}</span>
          <span>{training.is_base ? "TISIT" : "Моя тренировка"}</span>
        </div>
      </div>
    </section>
  );
};

const WorkoutMuscleGroupsSection = ({
  training,
  muscleItems,
  renderMuscleCard,
}: {
  training: Training;
  muscleItems: TrainingDetailsMuscleItem[];
  renderMuscleCard: TrainingDetailsMuscleRenderer;
}) => {
  const muscles = useMemo(
    () => getTrainingMuscleItems(training, muscleItems),
    [muscleItems, training],
  );

  if (!muscles.length) {
    return null;
  }

  return (
    <section className="training-details-section">
      <h2>Группы мышц</h2>
      <div
        className="training-details-muscles"
        aria-label="Группы мышц тренировки"
      >
        {muscles.map((muscle) => (
          <Fragment key={muscle.code}>{renderMuscleCard(muscle)}</Fragment>
        ))}
      </div>
    </section>
  );
};

const WorkoutExerciseList = ({ training }: { training: Training }) => {
  const exercises = useMemo(
    () =>
      [...training.exercises]
        .sort((a, b) => a.order - b.order)
        .map(mapTrainingExerciseToParams),
    [training.exercises],
  );

  return (
    <section className="training-details-section">
      <h2>Упражнения ({exercises.length})</h2>
      <ExercisesAccordion
        exerciseParams={exercises}
        setExerciseParams={() => undefined}
        addedExercises={exercises}
        onDelete={() => undefined}
        isReadonly
        expandReadonlyByDefault={false}
        className="training-details-exercises"
      />
    </section>
  );
};

const DeleteWorkoutConfirmModal = ({
  isOpen,
  isDeleting,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="training-delete-confirm-overlay" role="presentation">
      <div
        className="training-delete-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="training-delete-title"
      >
        <h3 id="training-delete-title">Удалить тренировку?</h3>
        <p>
          Действие нельзя будет отменить. Тренировка исчезнет из вашей истории.
        </p>
        <div>
          <button type="button" onClick={onCancel} disabled={isDeleting}>
            Отмена
          </button>
          <button
            type="button"
            className="is-danger"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "Удаляем..." : "Удалить"}
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkoutDetailsActionBar = ({
  activeAction,
  isStarting,
  onDelete,
  onEdit,
  onStart,
}: {
  activeAction: ActiveDetailsAction;
  isStarting: boolean;
  onDelete: () => void;
  onEdit: () => void;
  onStart: () => void;
}) => {
  const handleStartClick = () => {
    if (isStarting) {
      return;
    }

    onStart();
  };

  return (
    <NavPill
      ariaLabel="Действия с тренировкой"
      className="training-details-action-bar"
    >
      <button
        type="button"
        className={`training-details-action-button is-delete-action ${activeAction === "delete" ? "is-active" : ""}`}
        onClick={onDelete}
        aria-label="Удалить тренировку"
      >
        <Trash2 aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`training-details-action-button is-edit-action ${activeAction === "edit" ? "is-active" : ""}`}
        onClick={onEdit}
        aria-label="Редактировать тренировку"
      >
        <Pencil aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`training-details-action-button is-start-action ${activeAction === "start" ? "is-active" : ""} ${isStarting ? "is-starting" : ""}`}
        onClick={handleStartClick}
        disabled={isStarting}
        aria-label="Запустить тренировку"
      >
        <Play aria-hidden="true" />
        <span>Старт</span>
      </button>
    </NavPill>
  );
};

const TrainingDetailsContent = ({
  training,
  muscleItems,
  onClose,
  refetchTraining,
  renderMuscleCard,
}: {
  training: Training;
  muscleItems: TrainingDetailsMuscleItem[];
  onClose: () => void;
  refetchTraining: () => void;
  renderMuscleCard: TrainingDetailsMuscleRenderer;
}) => {
  const startTraining = useStartTraining(
    training,
    `/training/${training.id_workout}`,
  );
  const updateTraining = useUpdateTraining();
  const deleteTraining = useDeleteTraining();
  const [activeAction, setActiveAction] = useState<ActiveDetailsAction>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const editExercises = useMemo(
    () =>
      [...training.exercises]
        .sort((a, b) => a.order - b.order)
        .map(mapTrainingExerciseToParams),
    [training.exercises],
  );

  const handleStart = () => {
    if (isStarting) {
      return;
    }

    setActiveAction("start");
    setIsStarting(true);
    void startTraining();
  };

  const handleEdit = () => {
    setActiveAction("edit");
    window.setTimeout(() => setIsEditOpen(true), 140);
  };

  const handleDelete = () => {
    setActiveAction("delete");
    setIsDeleteOpen(true);
  };

  const handleEditOpenChange = (isOpen: boolean) => {
    setIsEditOpen(isOpen);

    if (!isOpen) {
      setActiveAction(null);
    }
  };

  const confirmDelete = async () => {
    await deleteTraining.mutateAsync(training.id_workout);
    setIsDeleteOpen(false);
    onClose();
  };

  const updateCurrentTraining = (
    exercises: ExerciseParams[],
    title: string,
    description: string,
  ) => {
    void updateTraining
      .mutateAsync({
        workoutId: training.id_workout,
        title,
        description,
        exerciseParams: exercises,
      })
      .then(() => {
        refetchTraining();
      });
  };

  return (
    <>
      <div className="training-details-content">
        <WorkoutInfoHeader training={training} />
        <WorkoutMuscleGroupsSection
          training={training}
          muscleItems={muscleItems}
          renderMuscleCard={renderMuscleCard}
        />
        <WorkoutExerciseList training={training} />
      </div>
      <div className="training-details-bottom-scrim" aria-hidden="true" />
      <WorkoutDetailsActionBar
        activeAction={activeAction}
        isStarting={isStarting}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onStart={handleStart}
      />
      <DeleteWorkoutConfirmModal
        isOpen={isDeleteOpen}
        isDeleting={deleteTraining.isLoading}
        onCancel={() => {
          setIsDeleteOpen(false);
          setActiveAction(null);
        }}
        onConfirm={confirmDelete}
      />
      <FormTrainingModal
        isOpen={isEditOpen}
        setIsOpen={handleEditOpenChange}
        exercises={editExercises}
        prevTitle={training.name}
        prevDescription={training.description}
        onUpdate={updateCurrentTraining}
        title="Изменение тренировки"
        submitLabel="Сохранить изменения"
        hideSecondaryAction
      />
    </>
  );
};

export const TrainingDetailsSurface = ({
  workoutId,
  isValidWorkoutId,
  muscleItems,
  onDismiss,
  renderMuscleCard,
}: {
  workoutId: number;
  isValidWorkoutId: boolean;
  muscleItems: TrainingDetailsMuscleItem[];
  onDismiss: () => void;
  renderMuscleCard: TrainingDetailsMuscleRenderer;
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);
  const sheetRef = useRef<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sheetDragRef = useRef<{
    currentY: number;
    pointerId: number;
    startY: number;
  } | null>(null);

  const {
    data: training,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["training", workoutId],
    queryFn: () => TrainingsProxy.getTrainingById(workoutId),
    enabled: isValidWorkoutId,
  });

  const setSheetDragY = (value: number) => {
    sheetRef.current?.style.setProperty(
      "--training-details-sheet-y",
      `${Math.round(value)}px`,
    );
  };

  const settleSheetDrag = () => {
    setIsDraggingSheet(false);
    window.requestAnimationFrame(() => setSheetDragY(0));
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const closeSheet = () => {
    if (isClosing) {
      return;
    }

    sheetRef.current?.style.removeProperty("--training-details-sheet-y");
    sheetDragRef.current = null;
    setIsDraggingSheet(false);
    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(onDismiss, CLOSE_ANIMATION_MS);
  };

  const handleDragPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (isClosing || (event.pointerType === "mouse" && event.button !== 0)) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    sheetDragRef.current = {
      currentY: 0,
      pointerId: event.pointerId,
      startY: event.clientY,
    };
    setIsDraggingSheet(true);
  };

  const handleDragPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const dragState = sheetDragRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const nextY = Math.max(0, event.clientY - dragState.startY);
    dragState.currentY = nextY;
    setSheetDragY(nextY);
  };

  const handleDragPointerEnd = (event: PointerEvent<HTMLButtonElement>) => {
    const dragState = sheetDragRef.current;

    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const shouldClose = dragState.currentY >= DRAG_CLOSE_DISTANCE;
    sheetDragRef.current = null;

    if (shouldClose) {
      closeSheet();
      return;
    }

    settleSheetDrag();
  };

  const handleDragPointerCancel = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    sheetDragRef.current = null;
    settleSheetDrag();
  };

  return (
    <>
      <button
        type="button"
        className={`training-details-backdrop ${isClosing ? "is-closing" : ""}`}
        aria-label="Закрыть тренировку"
        onClick={closeSheet}
      />
      <section
        ref={sheetRef}
        className={`training-details-sheet ${isClosing ? "is-closing" : ""} ${isDraggingSheet ? "is-dragging" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="training-details-title"
      >
        <button
          type="button"
          className="training-details-handle-button"
          aria-label="Потянуть вниз, чтобы закрыть тренировку"
          onPointerDown={handleDragPointerDown}
          onPointerMove={handleDragPointerMove}
          onPointerUp={handleDragPointerEnd}
          onPointerCancel={handleDragPointerCancel}
        >
          <span className="training-details-handle" aria-hidden="true" />
        </button>
        <header className="training-details-header">
          <h1 id="training-details-title">Тренировка</h1>
          <button
            type="button"
            onClick={closeSheet}
            aria-label="Закрыть тренировку"
          >
            <X aria-hidden="true" />
          </button>
        </header>

        {isLoading && (
          <div className="training-details-state">Загружаем тренировку...</div>
        )}
        {!isLoading && (!isValidWorkoutId || !training) && (
          <div className="training-details-state">
            Не удалось открыть тренировку. Попробуйте вернуться к списку.
          </div>
        )}
        {!isLoading && training && (
          <TrainingDetailsContent
            training={training}
            muscleItems={muscleItems}
            onClose={closeSheet}
            refetchTraining={() => {
              void refetch();
            }}
            renderMuscleCard={renderMuscleCard}
          />
        )}
      </section>
    </>
  );
};

export const TrainingDetailsOverlay = ({
  workoutId,
  muscleItems,
  onDismiss,
  renderMuscleCard,
}: {
  workoutId: number;
  muscleItems: TrainingDetailsMuscleItem[];
  onDismiss: () => void;
  renderMuscleCard: TrainingDetailsMuscleRenderer;
}) => (
  <TrainingDetailsSurface
    workoutId={workoutId}
    isValidWorkoutId={Number.isFinite(workoutId) && workoutId > 0}
    muscleItems={muscleItems}
    onDismiss={onDismiss}
    renderMuscleCard={renderMuscleCard}
  />
);
